import type { NumberStyle, PreferenceValues } from '../contract/types';
import { numberFormat } from './intl-cache';

/**
 * Rendering a bare number in the user's locale.
 *
 * This is the one formatter that needs no unit label, which is why it lands ahead of the rest of
 * the presentation layer: everything it needs is a locale and a digit count. Grouping separators
 * and the decimal mark come from `Intl`, so a French user reads `1 234,5` where an American reads
 * `1,234.5` without either being tabulated here.
 *
 * Digits are applied as a fixed width rather than a maximum. A column of `80.0` and `79.5` lines up
 * and reads as one series; a column of `80` and `79.5` reads as two.
 */

/**
 * What each number style is worth in fractional digits.
 *
 * The backend sends a style rather than a count — `DECIMAL_2` carries the pattern `%,.2f` — so the
 * count has to be recovered from it. The three styles are the whole vocabulary `Formatting.java`
 * defines, and the closed union means adding a fourth is a compile error here rather than a silent
 * fallback at runtime.
 */
export const DIGITS_BY_NUMBER_STYLE: Readonly<Record<NumberStyle, number>> = {
  INTEGER: 0,
  DECIMAL_2: 2,
  DECIMAL_3: 3,
};

/**
 * How the number is written, as opposed to what it is worth.
 *
 * - `standard` — the plain figure.
 * - `percent` — **the value is a fraction**, so `0.15` renders as `15%`. A percent point
 *   such as `85` belongs on `format.percent`.
 * - `compact` — `47.3K`, for tiles and axis labels where the exact figure is not the point.
 */
export type NumberNotation = 'standard' | 'percent' | 'compact';

export type NumberFormatOptions = {
  /**
   * Fractional digits.
   *
   * Read as a fixed width under `standard` and `percent`, so `2` pads `1.5` to `1.50`, and it
   * defaults to the user's number style. Compact notation reads the same number as a ceiling
   * instead — see `COMPACT_MAXIMUM_DIGITS` for why — and defaults to one digit rather than to
   * the style.
   */
  readonly digits?: number;
  /** Defaults to `standard`. */
  readonly notation?: NumberNotation;
};

/**
 * Compact notation caps its digits rather than fixing them.
 *
 * Everywhere else a fixed width is what makes a column readable, but compact exists to be short:
 * `1.2K` beside `47K` is the intent, and `1.2K` beside `47.0K` defeats it. One digit is what CLDR's
 * own compact patterns assume.
 */
const COMPACT_MAXIMUM_DIGITS = 1;

/**
 * Formats a number for display.
 *
 * Non-finite input is handed to `Intl` unchanged, which renders `NaN` and `∞`. Whether a reader
 * should ever see those is a presentation question — an empty cell is usually the better answer —
 * and it belongs with the components that know what the cell is for, not here.
 *
 * @param preferences - The resolved preferences supplying the locale and the default digit count.
 * @param value - The number to render.
 * @param options - Overrides for this one call.
 * @returns The formatted string.
 */
export const formatNumber = (
  preferences: PreferenceValues,
  value: number,
  options?: NumberFormatOptions
): string => {
  const notation = options?.notation ?? 'standard';

  if (notation === 'compact') {
    return numberFormat(preferences.locale, {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: options?.digits ?? COMPACT_MAXIMUM_DIGITS,
    }).format(value);
  }

  const digits =
    options?.digits ?? DIGITS_BY_NUMBER_STYLE[preferences.formats.number];

  return numberFormat(preferences.locale, {
    ...(notation === 'percent' ? { style: 'percent' as const } : {}),
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};
